import React, { ReactElement, ReactNode, useState } from 'react'
import styled from 'styled-components'
import { Prompt } from 'react-router-dom'
import theme from '../lib/theme'
import { StateAction } from '../types/typeAliases'
import Header from './header/Header'
import Menu from './menu/Menu'
import { User } from '../types/organisation'
import useIsMobile from '../helpers/isMobile'
import colors from '../lib/colors'

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'headerArea headerArea'
    'menuArea pageArea';
  .headerArea {
    grid-area: headerArea;
    height: ${theme.headerHeight};
  }
  .menuArea {
    grid-area: menuArea;
  }
  .pageArea {
    grid-area: pageArea;
    /* To make room for breadcrumbs */
    position: relative;
    padding-top: 20px;
  }
  background: ${colors.lightGray};
`

const Content = styled.div`
  padding: 0 30px 50px;
  box-sizing: border-box;
`
const WarningBanner = styled.div`
  height: ${theme.headerHeight};
  background-color: ${colors.red};
  border-radius: 5px;
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 1.3rem;
  padding: 15px;
  margin-top: 20px;
  box-sizing: border-box;
  div {
    margin-top: 3px;
    font-size: 1rem;
  }
`
export interface IPageProps {
  children: ReactNode
  warnBeforeExit?: boolean
  setLoading?: StateAction<boolean>
  primaryColor: string
  secondaryColor: string
  switcher?: ReactElement
  user: User
  menuContent: ReactElement
  logo: ReactNode
  breadcrumbs: ReactNode
  warnings?: string[]
  env?: string
}
function Page(props: IPageProps): ReactElement {
  const {
    children,
    warnBeforeExit,
    setLoading,
    primaryColor,
    secondaryColor,
    switcher,
    user,
    menuContent,
    logo,
    breadcrumbs,
    warnings,
    env,
  } = props

  const isMobile = useIsMobile()

  const init = localStorage.getItem('menu-preference') === 'collapsed' ?? false
  const [menuCollapsed, setMenuCollapsed] = useState<boolean>(init)

  const toggleMenu = (open?: boolean): void => {
    if (typeof open === 'boolean') {
      localStorage.setItem('menu-preference', open ? 'open' : 'collapsed')
      setMenuCollapsed(!open)
      return
    }

    setMenuCollapsed((prevState) => {
      const shouldCollapse = !prevState
      localStorage.setItem(
        'menu-preference',
        shouldCollapse ? 'collapsed' : 'open',
      )
      return shouldCollapse
    })
  }

  const prompt = (
    <Prompt
      message={(): string => {
        if (setLoading) setLoading(false)
        return 'You have unsaved changes, are you sure you want to leave?'
      }}
    />
  )

  const renderWarnings = warnings
    && warnings.length > -1
    && warnings.map((field, index) => {
      if (index === 5) return `and ${warnings.length - index} more.`
      if (index > 5) return null
      if (index === warnings.length - 1) return `${field}.`
      return `${field}, `
    })

  const plural = warnings && warnings.length > 1 ? 's' : ''
  const renderWarningBanner = warnings && warnings.length > 0 ? (
    <WarningBanner>
      <strong>
        {`You must complete ${warnings.length} field${plural} to activate this organisation:`}
      </strong>
      <div>{renderWarnings}</div>
    </WarningBanner>
  ) : null

  const isStaging = Boolean(env && env.includes('one-tree.dev/api'))

  return (
    <Container theme={{ warnings }}>
      {warnBeforeExit && prompt}
      <header className="headerArea">
        <Header
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          switcher={switcher}
          user={user}
          isMobile={isMobile}
          menuCollapsed={menuCollapsed}
          toggleMenu={toggleMenu}
          logo={logo}
          isStaging={isStaging}
        />
      </header>
      <nav className="menuArea">
        <Menu
          content={menuContent}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          menuCollapsed={menuCollapsed}
          toggleMenu={toggleMenu}
        />
      </nav>
      <main className="pageArea">
        {breadcrumbs}
        <Content>
          {renderWarningBanner}
          {children}
        </Content>
      </main>
    </Container>
  )
}
export default Page
